import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import CTASection from '../components/CTASection';
import { CheckCircle2, ArrowRight, FolderKanban, Calendar, Users, BarChart3, Clock, CheckSquare } from 'lucide-react';

const ZohoProjects = () => {
  const features = [
    {
      icon: <FolderKanban className="w-6 h-6 text-blue-700" />,
      title: 'Project Planning',
      description: 'Maak projectplannen met taken, mijlpalen en afhankelijkheden.',
    },
    {
      icon: <CheckSquare className="w-6 h-6 text-blue-700" />,
      title: 'Taakbeheer',
      description: 'Wijs taken toe, volg voortgang en deadline in realtime.',
    },
    {
      icon: <Calendar className="w-6 h-6 text-blue-700" />,
      title: 'Gantt Charts',
      description: 'Visualiseer project timelines met interactieve Gantt charts.',
    },
    {
      icon: <Clock className="w-6 h-6 text-blue-700" />,
      title: 'Tijdregistratie',
      description: 'Houd gewerkte uren bij per project en taak.',
    },
    {
      icon: <Users className="w-6 h-6 text-blue-700" />,
      title: 'Team Samenwerking',
      description: 'Communiceer en werk samen in één centrale omgeving.',
    },
    {
      icon: <BarChart3 className="w-6 h-6 text-blue-700" />,
      title: 'Project Rapportages',
      description: 'Krijg inzicht in project voortgang en resource gebruik.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
                Projectmanagement Software
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Zoho Projects: Beheer Projecten Zoals een Pro
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Houd projecten op schema met Zoho Projects. Plan, track en lever projecten op tijd en binnen budget 
                met krachtige projectmanagement tools.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                  >
                    Vraag een Demo Aan
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </Button>
                </Link>
                
                <a href="tel:0638823471">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-blue-700 text-blue-700 hover:bg-blue-50 px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300"
                  >
                    Bel: 06 3882 3471
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1700887937204-69f8b8400ace?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHw0fHxzb2Z0d2FyZSUyMGludGVyZmFjZXxlbnwwfHx8fDE3NzM4MTc2MjR8MA&ixlib=rb-4.1.0&q=85"
                alt="Zoho Projects Dashboard"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is Zoho Projects Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Wat is Zoho Projects?
            </h2>
            <div className="prose prose-lg text-gray-600 space-y-4">
              <p>
                Zoho Projects is een complete projectmanagement oplossing die teams helpt om effectiever samen te werken 
                en projecten succesvol af te ronden. Met Zoho Projects krijgt u alle tools die u nodig heeft voor planning, 
                uitvoering en rapportage van projecten.
              </p>
              <p>
                Als Zoho Partner implementeert Guijs Consultancy Zoho Projects zo dat het perfect aansluit op uw manier 
                van werken. Of u nu werkt met Agile, Waterfall of een hybride aanpak - wij configureren het systeem volgens 
                uw projectmethodologie.
              </p>
              <p>
                Zoho Projects integreert naadloos met Zoho CRM voor klantprojecten, met Zoho Books voor project facturatie, 
                en met andere tools die u al gebruikt. Dit zorgt voor één waarheid over al uw projecten.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Krachtige Functies van Zoho Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Alles wat u nodig heeft voor succesvol projectmanagement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={`item-${index}`} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/1181341/pexels-photo-1181341.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Project Management"
                className="rounded-2xl shadow-xl"
              />
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Voordelen van Zoho Projects
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Zoho Projects helpt teams om efficiënter te werken en projecten succesvol af te ronden binnen tijd en budget.
              </p>

              <ul className="space-y-4">
                {[
                  'Lever projecten op tijd met duidelijke planning en deadlines',
                  'Verbeter team samenwerking met centrale communicatie',
                  'Krijg realtime inzicht in project voortgang',
                  'Voorkom resource conflicten met workload management',
                  'Verhoog project winstgevendheid door betere tracking',
                  'Schaal van kleine tot grote enterprise projecten',
                ].map((benefit, index) => (
                  <li key={`item-${index}`} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Waarom Guijs Consultancy voor Zoho Projects?
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Wij helpen u om Zoho Projects optimaal in te zetten voor uw projectorganisatie.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Methode Configuratie</h3>
                  <p className="text-gray-600">
                    Wij configureren Zoho Projects volgens uw project methodologie (Agile, Waterfall, hybrid).
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Templates & Workflows</h3>
                  <p className="text-gray-600">
                    Wij maken project templates en workflows die uw standaard processen ondersteunen.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Integraties</h3>
                  <p className="text-gray-600">
                    Koppeling met Zoho CRM, Zoho Books en andere tools voor end-to-end project management.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Team Onboarding</h3>
                  <p className="text-gray-600">
                    Uitgebreide training en change management voor succesvolle adoptie.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection 
        title="Klaar voor Beter Projectmanagement?"
        subtitle="Ontdek hoe Zoho Projects uw team kan helpen om projecten succesvol af te ronden."
      />
    </div>
  );
};

export default ZohoProjects;
